import { useState, useEffect, useRef } from 'react';

export const INFO_MAPPING = {
  fullName: "",
  username: "Unique identifier — auto-generated from your name, but you can edit it.",
  email: "Type your registered email and press Enter.",
  brandName: "Your registered company name or brand name",
  websiteUrl: "Your Website URL",
  businessDescription: "Describe your business, who it's for, and what people might search on Google to find you. Generate a draft automatically and customize it to accurately reflect your business.",
  businessType: "Drives SEO engine, GMB optimization, and content personalization",
  primaryCategory: "The main industry category your brand falls under.",
  agencyName: "Name of the agency managing your brand account",
  agencyContactName: "Full name of the primary contact person at the agency",
  agencyEmail: "Email address of the agency for communication and reports",
  agencyPhone: "Phone number of the agency contact person",
  agencyWebsite: "Official website URL of the agency (Optional)",
  isManagedByAgency: "Enable if an external agency handles your marketing activities",
  findAgency: "Select from agencies already using Marketing4Sight",

  // Page 2
  brandLogo: "PNG or SVG, transparent background, min 200x200px",
  primaryFont: "Headings font used for your brand's visual identity",
  secondaryFont: "Body text font used for your brand's visual identity",
  primaryColor: "The dominant color representing your brand",
  secondaryColor: "The supporting color representing your brand",
  homePageUrl: "The primary URL of your business website (Optional)",
  productPageUrl: "The URL where your products are listed (Optional)",
  servicePageUrl: "The URL where your services are listed (Optional)",
  contactPageUrl: "The URL of your website's contact page (Optional)",
  aboutPageUrl: "The URL of your website's about page (Optional)",
  blogPageUrl: "The URL of your website's blog page (Optional)",
  socialPlatforms: "Add your social platform URLs (Optional)",

  // Page 3
  businessReach: "The geographic scope of your business operations",
  nationalOption: "Select the geographic coverage of your business",
  nationalCities: "Select specific cities or regions for national coverage",
  localLocation: "Autocomplete enabled — type to search or enter custom location. You can add multiple areas.",
  audienceType: "Defines whether your primary audience is B2B, B2C, or both",
  personaDetails: "Provide more details about your ideal customer",
  ageRange: "The age range of your target customer",
  gender: "The gender demographic of your target customer",
  interests: "Target customer interests and hobbies",
  profession: "Target customer professions and job roles",
  personaDescription: "Describe your typical customer — what they care about and why they choose you",
  physicalPresence: "Does your business have a physical location customers can visit?",
  brandIdentity: "Your brand identity (non-blocking)",
  competitors: "Add up to 5 competitors you want to benchmark against.",
};

interface FieldInfoProps {
  fieldKey: keyof typeof INFO_MAPPING;
}

export default function FieldInfo({ fieldKey }: FieldInfoProps) {
  const [isClicked, setIsClicked] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const content = INFO_MAPPING[fieldKey];

  useEffect(() => {
    if (!isClicked) return;

    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsClicked(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isClicked]);

  if (!content) return null;

  const show = isClicked || isHovered;

  return (
    <div ref={containerRef} className="relative inline-flex items-center">
      <button
        type="button"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsHovered(true)}
        onBlur={() => setIsHovered(false)}
        onClick={() => setIsClicked(!isClicked)}
        className={`inline-flex items-center justify-center transition-all duration-150 cursor-pointer ${show
          ? "text-teal-500"
          : "text-gray-400 hover:text-teal-500"
          }`}
        aria-label="Field information"
      >
        <i className="ri-information-line text-[16px] leading-none"></i>
      </button>
      {show && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-64 p-3 bg-teal-50 border border-teal-500 text-teal-950 text-xs font-normal rounded-xl shadow-lg shadow-teal-950/10 backdrop-blur-sm pointer-events-none transition-all duration-200">
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 w-2 h-2 bg-teal-50 border-r border-b border-teal-500 rotate-45"></div>
          <span className="relative z-10 block text-center leading-normal">{content}</span>
        </div>
      )}
    </div>
  );
}
